import { useMemo } from 'react'
import {
  courseCodeAndName,
  courseNameWithCode,
  identifierOrDash,
  labelledIdentifier,
} from '@/lib/identifiers'
import { useIdentifierPolicy, type IdentifierPolicy } from './useSchoolSettings'

/**
 * The school's own identifiers, rendered under the school's own settings.
 *
 * `lib/identifiers.ts` answers one question - is there a value here - and every
 * screen used to ask only that. It is no longer enough. A school that turns
 * student numbers off still has numbers stored against the children it enrolled
 * before it did: the setting stops the app writing them, deliberately, so that
 * turning it back on finds them again (see `services/identifierPolicy.ts`). Read
 * straight off the row, those values would keep appearing in a column the
 * settings page says the school does not have, that no form can edit, and that
 * empties itself one record at a time as the roster is re-saved.
 *
 * So this hook binds the policy to the rendering, and every screen goes through
 * it rather than through the pure helpers. A field the school does not have is
 * absent, whatever the row carries.
 *
 * Two shapes, matching the two in the pure module:
 *
 *   - the identifier as the *subject* of a cell or a labelled row -
 *     {@link IdentifierText.studentId} and friends. These return '' where the
 *     school has no such field, because there should be no cell there at all:
 *     guard the whole column or row with `shows` and never render the call.
 *   - the identifier as *decoration* on a name - the two course helpers. These
 *     drop the code and its punctuation together, so "Mathematics (MATH101)"
 *     becomes "Mathematics" rather than "Mathematics ()".
 */
export interface IdentifierText {
  /**
   * Which fields this school has. Guard a whole column, detail row or form input
   * with it - a value suppressed to '' still leaves an empty cell behind, and an
   * empty cell under a "Student ID" header reads as data that failed to load.
   */
  shows: IdentifierPolicy
  /** A student number as the subject of its own cell: the value, or an em dash. */
  studentId(value: string | null | undefined): string
  /** An employee number as the subject of its own cell. */
  employeeId(value: string | null | undefined): string
  /** A course code as the subject of its own cell. */
  courseCode(value: string | null | undefined): string
  /** "ID: S-1234" for a card or a picker option, or null when there is none. */
  studentIdLabelled(value: string | null | undefined): string | null
  /** "CODE - Name", or the name alone where the school has no codes. */
  courseCodeAndName(course: { courseCode?: string | null; courseName?: string | null }): string
  /** "Name (CODE)", or the name alone where the school has no codes. */
  courseNameWithCode(course: { courseCode?: string | null; courseName?: string | null }): string
}

/** The value to render for a field, or null where the school does not have it. */
function shown(enabled: boolean, value: string | null | undefined): string | null | undefined {
  return enabled ? value : null
}

export function useIdentifierText(): IdentifierText {
  // Destructured so the memo depends on the three booleans rather than on the
  // object literal `useIdentifierPolicy` builds fresh on every render.
  const { studentId, employeeId, courseCode } = useIdentifierPolicy()

  return useMemo(() => {
    // Suppressed before the helper rather than after it: `identifierOrDash` would
    // otherwise answer with a dash, which says "this record has no number" - a
    // claim about the record, where the truth is about the school.
    const subject = (enabled: boolean) => (value: string | null | undefined) =>
      enabled ? identifierOrDash(value) : ''

    // The code alone is taken away, so the helpers see a course with no code and
    // drop the punctuation that goes with it.
    const withoutSuppressedCode = (course: {
      courseCode?: string | null
      courseName?: string | null
    }) => ({ ...course, courseCode: shown(courseCode, course.courseCode) ?? null })

    return {
      shows: { studentId, employeeId, courseCode },
      studentId: subject(studentId),
      employeeId: subject(employeeId),
      courseCode: subject(courseCode),
      studentIdLabelled: (value) => labelledIdentifier('ID', shown(studentId, value)),
      courseCodeAndName: (course) => courseCodeAndName(withoutSuppressedCode(course)),
      courseNameWithCode: (course) => courseNameWithCode(withoutSuppressedCode(course)),
    }
  }, [studentId, employeeId, courseCode])
}
